// core/modals/expenses/ViewExpenseCategory.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { 
    X, 
    Folder,
    Briefcase,
    Home,
    Zap,
    Users,
    MapPin,
    TrendingUp,
    Tool,
    Calendar,
    Circle
} from 'feather-icons-react/build/IconComponents';
import { expenseCategoryActions } from '../../action/expenseCategoryActions';

const ViewExpenseCategory = ({ category, onClose }) => {
    
    // Get category icon
    const getCategoryIcon = (iconName, color) => {
        const icons = {
            'Folder': <Folder size={24} />,
            'Briefcase': <Briefcase size={24} />,
            'Home': <Home size={24} />,
            'Zap': <Zap size={24} />,
            'Users': <Users size={24} />,
            'MapPin': <MapPin size={24} />,
            'TrendingUp': <TrendingUp size={24} />,
            'Tool': <Tool size={24} />
        };
        return (
            <span style={{ color: color }}>
                {icons[iconName] || <Circle size={24} />}
            </span>
        );
    };
    
    return (
        <Modal show={true} onHide={onClose} size="md" centered>
            <Modal.Header className="border-0 pb-0">
                <Modal.Title>
                    <h5 className="mb-0">Category Details</h5>
                </Modal.Title>
                <button type="button" className="btn-close" onClick={onClose}>
                    <X size={18} />
                </button>
            </Modal.Header>
            <Modal.Body>
                <div className="card border-0">
                    <div className="card-body">
                        <div className="text-center mb-4">
                            <div 
                                className="rounded-circle p-3 d-inline-flex align-items-center justify-content-center mb-3"
                                style={{ backgroundColor: (category.color || '#6c757d') + '20', width: '80px', height: '80px' }}
                            >
                                {getCategoryIcon(category.icon, category.color)}
                            </div>
                            <h4>{category.category_name}</h4>
                            <p className="text-muted">{category.category_slug}</p>
                            {category.is_default === 1 && (
                                <span className="badge bg-secondary">Default Category</span>
                            )}
                            <span className={`badge ${category.status === 'active' ? 'bg-success' : 'bg-danger'} ms-2`}>
                                {category.status?.toUpperCase()}
                            </span>
                        </div>
                        
                        <div className="row">
                            <div className="col-12 mb-3">
                                <div className="bg-light p-3 rounded">
                                    <small className="text-muted d-block">Description</small>
                                    <p className="mb-0">{category.description || 'No description provided'}</p>
                                </div>
                            </div>
                            
                            <div className="col-6 mb-3">
                                <div className="bg-light p-3 rounded">
                                    <small className="text-muted d-block">Icon</small>
                                    <div className="d-flex align-items-center">
                                        <div className="me-2">{getCategoryIcon(category.icon, category.color)}</div>
                                        <span>{category.icon || 'Folder'}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-6 mb-3">
                                <div className="bg-light p-3 rounded">
                                    <small className="text-muted d-block">Color</small>
                                    <div className="d-flex align-items-center">
                                        <span 
                                            className="me-2"
                                            style={{ 
                                                display: 'inline-block',
                                                width: '20px',
                                                height: '20px',
                                                backgroundColor: category.color || '#6c757d',
                                                borderRadius: '4px'
                                            }}
                                        ></span>
                                        <span>{category.color || '#6c757d'}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-12 mb-3">
                                <div className="bg-light p-3 rounded">
                                    <small className="text-muted d-block">Created</small>
                                    <div className="d-flex align-items-center">
                                        <Calendar size={16} className="me-2 text-muted" />
                                        <span>{expenseCategoryActions.formatDate(category.created_at)}</span>
                                    </div>
                                </div>
                            </div>
                            
                            {category.updated_at && category.updated_at !== category.created_at && (
                                <div className="col-12 mb-3">
                                    <div className="bg-light p-3 rounded">
                                        <small className="text-muted d-block">Last Updated</small>
                                        <div className="d-flex align-items-center">
                                            <Calendar size={16} className="me-2 text-muted" />
                                            <span>{expenseCategoryActions.formatDate(category.updated_at)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="card-footer bg-light">
                        <div className="d-flex justify-content-end">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

ViewExpenseCategory.propTypes = {
    category: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        category_name: PropTypes.string,
        category_slug: PropTypes.string,
        description: PropTypes.string,
        color: PropTypes.string,
        icon: PropTypes.string,
        is_default: PropTypes.number,
        status: PropTypes.string,
        created_at: PropTypes.string,
        updated_at: PropTypes.string
    }).isRequired,
    onClose: PropTypes.func.isRequired
};

export default ViewExpenseCategory;